'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { authApi, authStorage, User } from './api';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    token: string | null;
    login: (email: string, password: string) => Promise<void>;
    register: (name: string, email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
    user: null,
    loading: true,
    token: null,
    login: async () => { },
    register: async () => { },
    logout: async () => { },
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    // Initialize auth from stored token
    useEffect(() => {
        const initAuth = async () => {
            const storedToken = authStorage.getToken();
            const storedUser = authStorage.getUser();

            if (storedToken && storedUser) {
                try {
                    // Verify token is still valid
                    const userData = await authApi.getCurrentUser(storedToken);
                    setUser(userData);
                    setToken(storedToken);
                } catch (error) {
                    // Token invalid, clear storage
                    authStorage.removeToken();
                    authStorage.setUser(null);
                }
            }
            setLoading(false);
        };

        initAuth();
    }, []);

    const login = async (email: string, password: string) => {
        try {
            const response = await authApi.login({ email, password });
            setToken(response.accessToken);
            setUser({
                id: response.userId,
                email: response.email,
                name: response.name,
            });
            authStorage.setToken(response.accessToken);
            authStorage.setUser({
                id: response.userId,
                email: response.email,
                name: response.name,
            });
        } catch (error) {
            console.error('Login error', error);
            throw error;
        }
    };

    const register = async (name: string, email: string, password: string) => {
        try {
            const response = await authApi.register({ name, email, password });
            setToken(response.accessToken);
            setUser({
                id: response.userId,
                email: response.email,
                name: response.name,
            });
            authStorage.setToken(response.accessToken);
            authStorage.setUser({
                id: response.userId,
                email: response.email,
                name: response.name,
            });
        } catch (error) {
            console.error('Register error', error);
            throw error;
        }
    };

    const logout = async () => {
        try {
            // Clear JWT auth
            authStorage.removeToken();
            authStorage.setUser(null);
            setToken(null);
            setUser(null);
        } catch (error) {
            console.error('Error signing out', error);
        }
    };

    return (
        <AuthContext.Provider value={{ user, loading, token, login, register, logout }}>
            {children}
        </AuthContext.Provider>
    );
}
